import type { CSSProperties } from 'react'
import { detailToneToken } from '@/entities/booking'
import type { BookingDetailRow } from '@/entities/booking'
import type { BookingDetail } from '@/shared/api'
import { SectionCard } from '@/shared/ui'
import s from './BookingDetailsCard.module.css'

/**
 * Секция 6 — «Детали брони» (`prototype/Prototype.dc.html:2354`). Содержимое целиком задаёт сервер:
 * и набор строк, и подписи, и порядок — фронт не знает, что «Объект» идёт первым.
 *
 * ⚠️ `<dt>`/`<dd>` ЗАВЁРНУТЫ В `<dl>`: в макете они лежат голыми, а это невалидный HTML — оба
 * элемента обязаны иметь родителем `<dl>`. Записано в плане фазы как дефект макета, который мы не
 * переносим.
 *
 * ⚠️ ТОЧКА У СТРОКИ «Гость в CRM» РИСУЕТСЯ ПО ПОЛЮ `tone`, И ТОЛЬКО ПО НЕМУ. Оба обходных пути
 * лгали: захардкоженная точка зажглась бы над словом «не связан», а вычитанная из `value` погасла бы
 * от смены копирайта. Сервер отдаёт СМЫСЛ, палитру выбирает фронт (разбор — в
 * `entities/booking/model/details.ts`).
 */
export function BookingDetailsCard({ details }: { details: BookingDetail['details'] }) {
  return (
    <SectionCard className={s.card} eyebrow="Детали брони">
      <dl className={s.body}>
        {details.map((row) => (
          // Ключ — машинный идентификатор строки: `label` сервер вправе переименовать, а второго
          // кандидата в контракте нет.
          <div key={row.key} className={s.row}>
            <dt className={s.key}>{row.label}</dt>
            <dd className={s.value}>
              <DetailValue row={row} />
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  )
}

/**
 * Значение строки: текст и, если он адрес, ссылка.
 *
 * ⚠️ ССЫЛКОЙ СТАНОВИТСЯ ЗНАЧЕНИЕ ЦЕЛИКОМ. Контракт другого и не выражает: `value` — одна строка,
 * `href` — одна ссылка на неё, а резать значение по разделителю значило бы парсить ЧЕЛОВЕЧЕСКУЮ
 * строку («Авито · через RealtyCalendar»), которую сервер вправе поменять, — ровно тот класс ошибок,
 * от которого защищает лежащий рядом `key`.
 *
 * ⚠️ МАКЕТ ЗДЕСЬ ПОКАЗЫВАЕТ УШЕДШУЮ ФОРМУ: в нём ссылочный стиль (моно-11px, акцент) отнесён к хвосту
 * `RealtyCalendar · rc-171840436`, а внешний номер из значения убран по §8 и живёт в «Данных
 * источника». Подчёркивания в макете нет ни у одной ссылки. Значению остаётся акцентный цвет.
 *
 * ⚠️ АДРЕС ВНЕШНИЙ: `target="_blank"` — потому что ведёт к тому, кто владеет броней у себя, а
 * `rel="noopener noreferrer"` обязателен — без него открытая вкладка получает `window.opener` и
 * может переписать нашу страницу на свою (tabnabbing). Схему `javascript:` React подменяет сам
 * (`sanitizeURL`, проверено в прод-сборке React 19); свой фильтр схем поверх библиотечного — вторая
 * копия защиты, которая однажды разойдётся с первой.
 */
function DetailValue({ row }: { row: BookingDetailRow }) {
  const tone = detailToneToken(row.tone)

  const value = row.href ? (
    <a className={s.link} href={row.href} target="_blank" rel="noopener noreferrer">
      {row.value}
    </a>
  ) : (
    row.value
  )

  return (
    <>
      {/*
       * Точка-индикатор слева от значения: есть тон — есть точка, `null` или отсутствие поля — точки
       * нет вовсе (так требует контракт). Обёртка со своим `gap` — из макета, там точка и текст лежат
       * в отдельном `inline-flex`.
       *
       * `aria-hidden` — точка ДУБЛИРУЕТ сказанное словом («связан · clients-service»), и озвучивать
       * её отдельно значило бы повторить скринридеру одно и то же дважды.
       *
       * Токен инлайн-переменной `--tint`, как у чипа статуса: один класс красится под любой тон, без
       * ветвления в CSS и без динамического `s[...]` по CSS-модулю.
       */}
      {tone ? (
        <span className={s.toned}>
          <span
            className={s.dot}
            style={{ '--tint': tone } as CSSProperties}
            aria-hidden="true"
          />
          {value}
        </span>
      ) : (
        value
      )}

      {/*
       * ⚠️ ПЕР-СТРОЧНОГО БЕЙДЖА РЕЕСТРА ЗДЕСЬ НЕТ, и заводить его не подо что — надгробие и то, что
       * надо помнить, если он понадобится снова, лежат в `entities/booking/model/details.ts`. Сам
       * `RoadmapBadge` жив и используется в четырёх местах вне карточки брони.
       */}
    </>
  )
}
